<template>
  <el-drawer v-model="open" :title="t('admin.theme.title')" size="320px" append-to-body>
    <div class="admin-theme-settings">
      <section>
        <h3>{{ t('admin.theme.sidebar') }}</h3>
        <div class="admin-theme-settings__previews">
          <button
            v-for="option in sidebarOptions"
            :key="option.value"
            type="button"
            :class="['admin-theme-settings__preview', `is-sidebar-${option.value}`, { 'is-active': modelValue.sidebar === option.value }]"
            :aria-label="option.label"
            @click="update({ sidebar: option.value })"
          ><span>{{ option.label }}</span></button>
        </div>
      </section>

      <el-divider />

      <section>
        <h3>{{ t('admin.theme.header') }}</h3>
        <div class="admin-theme-settings__previews">
          <button
            v-for="option in headerOptions"
            :key="option.value"
            type="button"
            :class="['admin-theme-settings__preview', `is-header-${option.value}`, { 'is-active': modelValue.header === option.value }]"
            :aria-label="option.label"
            @click="update({ header: option.value })"
          ><span>{{ option.label }}</span></button>
        </div>
      </section>

      <el-divider />

      <section>
        <h3>{{ t('admin.theme.primaryColor') }}</h3>
        <div class="admin-theme-settings__colors">
          <button
            v-for="color in ADMIN_THEME_COLORS"
            :key="color"
            type="button"
            :style="{ backgroundColor: color }"
            :class="['admin-theme-settings__color', { 'is-active': modelValue.primaryColor === color }]"
            :aria-label="t('admin.theme.colorLabel', { color })"
            @click="update({ primaryColor: color })"
          ><Check v-if="modelValue.primaryColor === color" /></button>
        </div>
      </section>

      <el-divider />

      <section>
        <h3>{{ t('admin.theme.tabs') }}</h3>
        <el-radio-group :model-value="modelValue.tabs" @change="updateTabs">
          <el-radio-button value="default">{{ t('admin.theme.simpleTabs') }}</el-radio-button>
          <el-radio-button value="card">{{ t('admin.theme.cardTabs') }}</el-radio-button>
        </el-radio-group>
      </section>

      <el-button class="admin-theme-settings__reset" :icon="RefreshLeft" @click="reset">
        {{ t('admin.theme.reset') }}
      </el-button>
    </div>
  </el-drawer>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { Check, RefreshLeft } from '@element-plus/icons-vue'
import { useI18n } from 'vue-i18n'

import {
  ADMIN_THEME_COLORS,
  DEFAULT_ADMIN_THEME,
  type AdminHeaderTheme,
  type AdminSidebarTheme,
  type AdminTabsTheme,
  type AdminThemeSettings
} from '@/apps/admin/utils/theme-settings'

const props = defineProps<{ modelValue: AdminThemeSettings }>()
const emit = defineEmits<{ 'update:modelValue': [value: AdminThemeSettings] }>()
const open = ref(false)
const { t } = useI18n()

const sidebarOptions = computed<Array<{ label: string, value: AdminSidebarTheme }>>(() => [
  { label: t('admin.theme.dark'), value: 'dark' },
  { label: t('admin.theme.light'), value: 'light' }
])
const headerOptions = computed<Array<{ label: string, value: AdminHeaderTheme }>>(() => [
  { label: t('admin.theme.primary'), value: 'primary' },
  { label: t('admin.theme.light'), value: 'light' },
  { label: t('admin.theme.dark'), value: 'dark' }
])

const update = (patch: Partial<AdminThemeSettings>): void => {
  emit('update:modelValue', { ...props.modelValue, ...patch })
}
const updateTabs = (value: string | number | boolean | undefined): void => {
  if (value === 'default' || value === 'card') update({ tabs: value as AdminTabsTheme })
}
const reset = (): void => emit('update:modelValue', { ...DEFAULT_ADMIN_THEME })
const show = (): void => { open.value = true }

defineExpose({ open: show })
</script>

<style scoped>
.admin-theme-settings { padding: 0 4px 24px; }
.admin-theme-settings h3 { margin: 0 0 14px; color: #374151; font-size: 14px; font-weight: 600; }
.admin-theme-settings__previews { display: flex; gap: 18px; }
.admin-theme-settings__preview { position: relative; width: 74px; height: 50px; padding: 0; overflow: visible; border: 2px solid transparent; border-radius: 5px; background: #f3f4f6; box-shadow: 0 1px 4px rgb(15 23 42 / 18%); cursor: pointer; }
.admin-theme-settings__preview::before { position: absolute; inset: 0 auto 0 0; width: 22px; background: #263238; content: ''; }
.admin-theme-settings__preview.is-sidebar-light::before { background: #fff; border-right: 1px solid #e5e7eb; }
.admin-theme-settings__preview[class*='is-header-']::before { inset: 0 0 auto; width: auto; height: 14px; }
.admin-theme-settings__preview.is-header-primary::before { background: var(--admin-preview-primary, #17b3a3); }
.admin-theme-settings__preview.is-header-light::before { background: #fff; border-bottom: 1px solid #e5e7eb; }
.admin-theme-settings__preview.is-header-dark::before { background: #263238; }
.admin-theme-settings__preview.is-active { border-color: #19be6b; }
.admin-theme-settings__preview span { position: absolute; top: calc(100% + 6px); left: 0; width: 100%; color: #64748b; font-size: 12px; }
.admin-theme-settings__colors { display: flex; flex-wrap: wrap; gap: 10px; }
.admin-theme-settings__color { display: grid; width: 28px; height: 28px; padding: 6px; place-items: center; border: 0; border-radius: 4px; color: #fff; cursor: pointer; }
.admin-theme-settings__color svg { width: 16px; height: 16px; }
.admin-theme-settings__color.is-active { outline: 2px solid #19be6b; outline-offset: 2px; }
.admin-theme-settings__reset { width: 100%; margin-top: 28px; }
.admin-theme-settings :deep(.el-divider) { margin: 32px 0 20px; }
</style>
